import { useState } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ShieldCheck, KeyRound, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Step1Pin({ workflow, request, requestId, onAdvance }) {
  const [pin, setPin] = useState("");
  const [verifying, setVerifying] = useState(false);
  const verified = workflow?.step_1?.verified_at;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!pin || pin.length < 4) {
      toast.error("Enter the customer's PIN");
      return;
    }
    setVerifying(true);
    try {
      await API.post(`/inspection/${requestId}/step/1/verify-pin`, { pin: pin.trim() });
      toast.success("PIN verified — customer info linked");
      onAdvance(2);
    } catch (err) {
      const msg = err?.response?.data?.detail || "PIN verification failed";
      toast.error(msg, { duration: 5000 });
    } finally {
      setVerifying(false);
    }
  };

  if (verified) {
    return (
      <div className="bg-white rounded-2xl border border-emerald-200 p-5 text-center">
        <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
        <h2 className="text-base font-bold text-zinc-900">Step 1 · Arrival Verified</h2>
        <p className="text-xs text-zinc-500 mt-1">PIN matched at {new Date(verified).toLocaleString()}</p>
        <Button onClick={() => onAdvance(2)} className="mt-4 h-11 bg-zinc-900 text-white" data-testid="step-1-next">
          Continue to Step 2
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl border border-zinc-200 p-5"
          data-testid="step-1-pin-form">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck className="w-5 h-5 text-amber-600" />
        <h2 className="text-base font-bold text-zinc-900">Step 1 · Arrival & PIN Verification</h2>
      </div>
      <p className="text-xs text-zinc-600 mb-4">
        Ask the customer for their job acceptance PIN. Entering the correct PIN confirms identity
        and auto-populates their information into this inspection.
      </p>

      <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Customer PIN</label>
      <div className="relative mt-1">
        <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
        <Input
          inputMode="numeric"
          pattern="[0-9]*"
          autoFocus
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
          placeholder="Enter PIN"
          className="pl-9 h-12 text-lg tracking-widest text-center font-mono"
          data-testid="step-1-pin-input"
        />
      </div>

      <Button type="submit" disabled={verifying || pin.length < 4}
              className="mt-4 w-full h-12 text-base bg-zinc-900 hover:bg-zinc-800 text-white disabled:bg-zinc-200 disabled:text-zinc-400"
              data-testid="step-1-verify-btn">
        {verifying ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying…</> : "Verify PIN"}
      </Button>
    </form>
  );
}
